/**
 * My Catalogues Page for ZAMMER Marketplace
 *
 * Lists every catalogue the seller has created (drafts + submitted + processed)
 * with status badges, product counts, and per-row actions:
 *   - draft: Resume Editing (→ /seller/upload-catalogue?resumeDraftId=<id>) + Delete
 *   - completed/partial/failed: View Details (inline expand) + Delete
 *   - processing/generating: read-only spinner
 *
 * Backend endpoints used:
 *   GET    /api/catalogues          (paginated)
 *   DELETE /api/catalogues/:id
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import SellerLayout from '../../components/layouts/SellerLayout';
import { getSellerCatalogues, deleteCatalogue } from '../../services/catalogueService';
import { updateProductStatus } from '../../services/productService';

/**
 * Catalogue search.
 *
 * A seller with 400+ products across dozens of catalogues had no way to find
 * one except scrolling. Every term must match something — so "red lehenga"
 * finds the red lehenga rather than everything red plus everything lehenga —
 * and a term may land on the catalogue (its name or category path) or on any
 * product inside it (name or SKU).
 *
 * Matching the catalogue itself counts for all of its products, so searching a
 * category still returns whole batches.
 */
const tokenize = (query) =>
  String(query || '').toLowerCase().split(/\s+/).filter(Boolean).slice(0, 8);

const haystack = (...parts) => parts.filter(Boolean).join(' ').toLowerCase();

const matchCatalogue = (catalogue, tokens) => {
  if (!tokens.length) {
    return { matches: true, productIds: null, matchedCount: catalogue.products?.length || 0 };
  }

  const catalogueText = haystack(catalogue.name, catalogue.category?.path, catalogue.hsnCode);
  const items = catalogue.products || [];

  // Terms the catalogue itself satisfies are already met for every product in it.
  const unmet = tokens.filter((t) => !catalogueText.includes(t));
  if (!unmet.length) {
    return { matches: true, productIds: null, matchedCount: items.length };
  }

  const matchedIds = new Set();
  items.forEach((item, idx) => {
    const itemText = haystack(item.name, item.sku, item.product?.name);
    if (unmet.every((t) => itemText.includes(t))) matchedIds.add(idx);
  });

  return {
    matches: matchedIds.size > 0,
    productIds: matchedIds,
    matchedCount: matchedIds.size
  };
};

const STATUS_BADGES = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  submitted: { label: 'Submitted', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  processing: { label: 'Processing…', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  generating: { label: 'AI photos…', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  partial: { label: 'Partial', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-800 border-red-200' }
};

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) +
           ' · ' +
           d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const MyCatalogues = () => {
  const navigate = useNavigate();
  const [catalogues, setCatalogues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [statusUpdatingId, setStatusUpdatingId] = useState(null);
  const [query, setQuery] = useState('');

  const tokens = useMemo(() => tokenize(query), [query]);

  // Each surviving catalogue carries the indices of the products that matched,
  // so the expanded list can show just those instead of all 20 again.
  const visibleCatalogues = useMemo(() => {
    return catalogues
      .map((catalogue) => ({ catalogue, ...matchCatalogue(catalogue, tokens) }))
      .filter((entry) => entry.matches);
  }, [catalogues, tokens]);

  const matchedProductCount = useMemo(
    () => visibleCatalogues.reduce((sum, e) => sum + e.matchedCount, 0),
    [visibleCatalogues]
  );

  /**
   * Pause / resume a single product from inside its catalogue (Change 12).
   *
   * `products.product` arrives populated with the live product (id, name,
   * images, status), so the toggle updates that in place rather than
   * refetching the whole list.
   */
  const handleTogglePause = async (item) => {
    const productId = item.product._id;
    const nextStatus = item.product.status === 'paused' ? 'active' : 'paused';
    setStatusUpdatingId(productId);
    try {
      const response = await updateProductStatus(productId, nextStatus);
      if (response.success) {
        toast.success(nextStatus === 'paused' ? 'Product paused' : 'Product resumed');
        setCatalogues((prev) =>
          prev.map((cat) => ({
            ...cat,
            products: (cat.products || []).map((p) =>
              String(p.product?._id) === String(productId)
                ? { ...p, product: { ...p.product, status: nextStatus } }
                : p
            )
          }))
        );
      } else {
        toast.error(response.message || 'Could not update the product');
      }
    } catch (err) {
      console.error('Toggle product status error:', err);
      toast.error(err.message || 'Could not update the product');
    } finally {
      setStatusUpdatingId(null);
      setOpenMenuId(null);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getSellerCatalogues({ page: 1, limit: 50 });
      if (response.success) {
        setCatalogues(response.data || []);
      } else {
        setError(response.message || 'Could not load catalogues');
      }
    } catch (err) {
      console.error('Load catalogues error:', err);
      setError(err.message || 'Could not load catalogues');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleResume = (catalogue) => {
    navigate(`/seller/upload-catalogue?resumeDraftId=${catalogue._id}`);
  };

  const handleDelete = async (catalogue) => {
    const confirmMsg = catalogue.status === 'draft'
      ? `Delete this draft? "${catalogue.name}" — this cannot be undone.`
      : `Delete this catalogue record? The Products it created will remain. Continue?`;
    if (!window.confirm(confirmMsg)) return;

    setDeletingId(catalogue._id);
    try {
      // For non-drafts the seller likely wants to keep the published Products.
      // We only pass deleteProducts=true if it's a failed catalogue with stranded
      // (incomplete) Product creation; otherwise leave them.
      const response = await deleteCatalogue(catalogue._id, false);
      if (response.success) {
        toast.success('Catalogue deleted');
        setCatalogues(prev => prev.filter(c => c._id !== catalogue._id));
        if (expandedId === catalogue._id) setExpandedId(null);
      } else {
        toast.error(response.message || 'Failed to delete catalogue');
      }
    } catch (err) {
      console.error('Delete catalogue error:', err);
      toast.error(err.message || 'Failed to delete catalogue');
    } finally {
      setDeletingId(null);
    }
  };

  const renderActions = (catalogue) => {
    const status = catalogue.status;
    const busy = deletingId === catalogue._id;

    if (status === 'processing' || status === 'generating') {
      return (
        <span className="text-xs text-gray-500 italic">Working in background…</span>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-2">
        {status === 'draft' && (
          <button
            onClick={() => handleResume(catalogue)}
            disabled={busy}
            className="px-3 py-1.5 bg-purple-600 text-white rounded-md text-xs font-medium hover:bg-purple-700 disabled:opacity-50"
          >
            Resume Editing →
          </button>
        )}
        {(status === 'completed' || status === 'partial' || status === 'failed' || status === 'submitted') && (
          <button
            onClick={() => setExpandedId(expandedId === catalogue._id ? null : catalogue._id)}
            disabled={busy}
            className="px-3 py-1.5 border border-gray-300 text-gray-700 rounded-md text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
          >
            {expandedId === catalogue._id ? 'Hide Details' : 'View Details'}
          </button>
        )}
        <button
          onClick={() => handleDelete(catalogue)}
          disabled={busy}
          className="px-3 py-1.5 border border-red-300 text-red-600 rounded-md text-xs font-medium hover:bg-red-50 disabled:opacity-50"
        >
          {busy ? 'Deleting…' : 'Delete'}
        </button>
      </div>
    );
  };

  // `matchedIds` is null when nothing is being searched (or when the catalogue
  // itself matched), meaning show everything; otherwise only the hits.
  const renderDetails = (catalogue, matchedIds) => (
    <div className="mt-4 border-t border-gray-100 pt-4">
      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {matchedIds
          ? `Matching products (${matchedIds.size} of ${catalogue.products?.length || 0})`
          : `Products in this catalogue (${catalogue.products?.length || 0})`}
      </p>
      <div className="space-y-2">
        {(catalogue.products || []).map((item, idx) => {
          if (matchedIds && !matchedIds.has(idx)) return null;
          const itemStatus = item.status || 'pending';
          const itemBadge = itemStatus === 'created'
            ? { label: 'Created', cls: 'bg-emerald-100 text-emerald-700' }
            : itemStatus === 'failed'
              ? { label: 'Failed', cls: 'bg-red-100 text-red-700' }
              : { label: 'Pending', cls: 'bg-gray-100 text-gray-700' };
          return (
            <div key={idx} className="flex items-center gap-3 p-2 bg-gray-50 rounded border border-gray-100">
              {item.images?.[0] && (
                <img src={item.images[0]} alt="" className="w-10 h-10 rounded object-cover object-top border border-white" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {item.name || `Product ${idx + 1}`}
                </p>
                <p className="text-xs text-gray-500">
                  ₹{item.zammerPrice} · MRP ₹{item.mrp} · {item.variants?.length || 0} variant(s)
                </p>
                {item.errorMessage && (
                  <p className="text-xs text-red-600 mt-0.5">⚠ {item.errorMessage}</p>
                )}
              </div>
              <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${itemBadge.cls}`}>
                {itemBadge.label}
              </span>

              {/* Change 12 — per-product Edit, plus More for variants/pause.
                  Only products that actually made it into the catalogue have a
                  product record to act on. */}
              {item.product?._id && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button
                    onClick={() => navigate(`/seller/edit-product/${item.product._id}`)}
                    className="text-xs font-medium text-orange-600 hover:text-orange-700 px-2 py-1 rounded hover:bg-orange-50"
                  >
                    Edit
                  </button>
                  <div className="relative">
                    <button
                      onClick={() => setOpenMenuId(openMenuId === item.product._id ? null : item.product._id)}
                      aria-label="More actions"
                      className="text-xs font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100"
                    >
                      More ▾
                    </button>
                    {openMenuId === item.product._id && (
                      <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1">
                        <button
                          onClick={() => {
                            setOpenMenuId(null);
                            navigate(`/seller/edit-product/${item.product._id}#variants`);
                          }}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50"
                        >
                          Add new variant (size)
                        </button>
                        <button
                          onClick={() => handleTogglePause(item)}
                          disabled={statusUpdatingId === item.product._id}
                          className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                          {statusUpdatingId === item.product._id
                            ? 'Updating…'
                            : item.product.status === 'paused'
                              ? 'Resume product'
                              : 'Pause product'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <SellerLayout>
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">My Catalogues</h1>
            <p className="text-gray-600 mt-1">
              All your catalogue uploads — drafts you can resume, and submitted batches with status.
            </p>
          </div>
          <button
            onClick={() => navigate('/seller/upload-catalogue')}
            className="px-4 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
          >
            + New Catalogue
          </button>
        </div>

        {/* Search — only worth showing once there is something to search through */}
        {!loading && !error && catalogues.length > 0 && (
          <div className="mb-6">
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
              </svg>
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by product name, SKU, catalogue or category…"
                aria-label="Search catalogues and products"
                className="w-full pl-10 pr-24 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              />
              {query && (
                <button
                  type="button"
                  onClick={() => setQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-semibold text-purple-600 hover:text-purple-800"
                >
                  Clear
                </button>
              )}
            </div>
            {tokens.length > 0 && (
              <p className="mt-2 text-xs text-gray-600">
                {visibleCatalogues.length === 0
                  ? 'No products or catalogues match that search.'
                  : `${matchedProductCount} product${matchedProductCount === 1 ? '' : 's'} in ${visibleCatalogues.length} catalogue${visibleCatalogues.length === 1 ? '' : 's'}`}
              </p>
            )}
          </div>
        )}

        {loading ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <div className="w-10 h-10 border-2 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-3" />
            <p className="text-gray-700 font-medium">Loading your catalogues…</p>
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <p className="text-red-800 font-medium mb-2">Something went wrong</p>
            <p className="text-red-600 text-sm mb-4">{error}</p>
            <button
              onClick={load}
              className="px-4 py-2 bg-red-600 text-white rounded-md text-sm font-medium hover:bg-red-700"
            >
              Try Again
            </button>
          </div>
        ) : catalogues.length === 0 ? (
          <div className="bg-white rounded-xl border p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            <p className="text-gray-800 font-semibold mb-1">No catalogues yet</p>
            <p className="text-gray-500 text-sm mb-5">Upload your first batch and it will appear here.</p>
            <button
              onClick={() => navigate('/seller/upload-catalogue')}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
            >
              Upload Catalogue
            </button>
          </div>
        ) : visibleCatalogues.length === 0 ? (
          /* Has catalogues, but the search excluded all of them. Distinct from
             the "No catalogues yet" state above — the way out is clearing the
             search, not uploading. */
          <div className="bg-white rounded-xl border p-12 text-center">
            <svg className="mx-auto h-12 w-12 text-gray-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
            </svg>
            <p className="text-gray-800 font-semibold mb-1">Nothing matches “{query.trim()}”</p>
            <p className="text-gray-500 text-sm mb-5">Try a product name, an SKU, or part of a category.</p>
            <button
              onClick={() => setQuery('')}
              className="px-5 py-2.5 bg-purple-600 text-white rounded-lg font-medium hover:bg-purple-700"
            >
              Clear search
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {visibleCatalogues.map(({ catalogue, productIds }) => {
              const badge = STATUS_BADGES[catalogue.status] || STATUS_BADGES.draft;
              const stats = catalogue.stats || {};
              const productCount = catalogue.products?.length || 0;
              const previewImages = (catalogue.products || [])
                .slice(0, 4)
                .map(p => p.images?.[0])
                .filter(Boolean);

              return (
                <div key={catalogue._id} className="bg-white rounded-xl border border-gray-200 p-5 hover:border-purple-200 transition-colors">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-gray-800 truncate">
                          {catalogue.name || 'Untitled Catalogue'}
                        </h3>
                        <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full border ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Created {formatDate(catalogue.createdAt)}
                      </p>
                      {catalogue.category?.path && (
                        <p className="text-xs text-purple-600 mt-1 font-medium">
                          {catalogue.category.path}
                        </p>
                      )}
                      <div className="mt-3 flex items-center gap-4 text-xs text-gray-600">
                        <span>
                          <span className="font-semibold">{productCount}</span> product slot{productCount !== 1 ? 's' : ''}
                        </span>
                        {catalogue.status !== 'draft' && (
                          <>
                            <span>·</span>
                            <span>
                              <span className="font-semibold">{stats.createdProducts || 0}</span> created
                            </span>
                            {(stats.failedProducts || 0) > 0 && (
                              <>
                                <span>·</span>
                                <span className="text-red-600">
                                  <span className="font-semibold">{stats.failedProducts}</span> failed
                                </span>
                              </>
                            )}
                          </>
                        )}
                      </div>
                    </div>

                    {previewImages.length > 0 && (
                      <div className="flex -space-x-2 flex-shrink-0">
                        {previewImages.map((url, idx) => (
                          <img
                            key={idx}
                            src={url}
                            alt=""
                            className="w-12 h-12 rounded-lg object-cover object-top border-2 border-white shadow-sm"
                          />
                        ))}
                        {productCount > previewImages.length && (
                          <div className="w-12 h-12 rounded-lg bg-gray-100 border-2 border-white shadow-sm flex items-center justify-center text-xs font-semibold text-gray-600">
                            +{productCount - previewImages.length}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-xs text-gray-400">
                      ID: <span className="font-mono">{catalogue._id?.slice(-8)}</span>
                    </div>
                    {renderActions(catalogue)}
                  </div>

                  {(expandedId === catalogue._id || (tokens.length > 0 && productIds))
                    && renderDetails(catalogue, tokens.length > 0 ? productIds : null)}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </SellerLayout>
  );
};

export default MyCatalogues;
